import axios, { AxiosInstance, AxiosError } from 'axios';
import { RefreshUnavailableError } from './auth/axios-auth';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

/** Billing Summary "Payment Update" modal -- one claim's invoice plus
 * whatever payment reconciliation has been recorded against it. Shared
 * shape between getBillingSummaryDetail and updateBillingPayment so the
 * modal can treat "load" and "just saved" results identically. */
export interface BillingInvoiceDetail {
  job_id: string;
  report_number: string;
  claim_number: string;
  vehicle_registration_no: string;
  invoice_number: string | null;
  sub_total: number | null;
  gst_amount: number;
  total_amount: number | null;
  total_receivable_amount: number | null;
  received_fees_amount: number | null;
  received_gst_amount: number | null;
  received_tds_amount: number | null;
  received_short_amount: number | null;
  payment_details: string | null;
  payment_received_date: string | null;
  payment_status: 'pending' | 'paid';
  total_received: number;
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add tenant ID if available
        const tenantId = this.getTenantId();
        if (tenantId) {
          config.headers['X-Tenant-ID'] = tenantId;
        }

        // Add user type context if available
        const userType = this.getUserType();
        if (userType) {
          config.headers['X-User-Type'] = userType;
        }

        // Add user roles if available
        const roles = this.getUserRoles();
        if (roles) {
          config.headers['X-User-Roles'] = roles;
        }

        // Add active role if user has switched roles
        const activeRole = this.getActiveRole();
        if (activeRole) {
          config.headers['X-Active-Role'] = activeRole;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Don't redirect if this is a login request (login failures are expected)
          const isLoginRequest = error.config?.url?.includes('/login');
          const isSuperAdminContext =
            this.isSuperAdminRequest(error.config?.url) ||
            (typeof window !== 'undefined' && localStorage.getItem('user_type') === 'super_admin');
          // A request already retried once after a refresh must not trigger another
          // refresh-and-retry cycle — a second 401 means the refreshed token is still
          // rejected (e.g. a backend auth bug unrelated to expiry), so retrying forever
          // would hammer the API. Fall through to the login redirect instead.
          const alreadyRetried = (error.config as { _retried?: boolean } | undefined)?._retried;

          if (!isLoginRequest) {
            // Determine which login page to redirect to based on current page
            const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
            const isOnSuperAdminPage = currentPath.startsWith('/super-admin');

            // Super admin tokens do not support the standard refresh path; clear and redirect.
            if (isSuperAdminContext) {
              this.clearAuthStorage();
              if (typeof window !== 'undefined') {
                // Redirect to appropriate login page based on where user currently is
                window.location.href = isOnSuperAdminPage ? '/super-admin/login' : '/login';
              }
              return Promise.reject(error);
            }

            if (!alreadyRetried) {
              // Token expired, try to refresh
              let refreshed = false;
              try {
                refreshed = await this.refreshToken();
              } catch (refreshErr) {
                if (refreshErr instanceof RefreshUnavailableError) {
                  // Couldn't even attempt the refresh (network/transient) --
                  // leave the session alone and just surface this one
                  // request's failure, same as a non-401 error would.
                  return Promise.reject(error);
                }
                throw refreshErr;
              }
              if (refreshed && error.config) {
                // Update the Authorization header with the new token
                const newToken = this.getToken();
                if (newToken) {
                  error.config.headers.Authorization = `Bearer ${newToken}`;
                }
                (error.config as { _retried?: boolean })._retried = true;
                return this.client.request(error.config);
              }
            }

            // Redirect to login only if not already on a login page
            // Use appropriate login page based on current location
            if (typeof window !== 'undefined') {
              window.location.href = isOnSuperAdminPage ? '/super-admin/login' : '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
  }

  private getTenantId(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('tenant_id');
  }

  private getUserType(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('user_type');
  }

  private getUserRoles(): string | null {
    if (typeof window === 'undefined') return null;
    const roles = localStorage.getItem('user_roles');
    return roles ? JSON.stringify(JSON.parse(roles)) : null;
  }

  private getActiveRole(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('active_role');
  }

  private getUserId(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('user_id');
  }

  // Resolves true/false for a genuine success/rejection (the server was
  // reached and either issued a new token or explicitly rejected the
  // refresh token). Throws RefreshUnavailableError if the request itself
  // couldn't be completed (network/transient, e.g. a laptop just woken
  // from sleep/lock with Wi-Fi/VPN still reconnecting) -- that case must
  // not be treated the same as a rejected token, or the caller logs the
  // user out over a blip that would have cleared up on its own.
  private async refreshToken(): Promise<boolean> {
    if (typeof window === 'undefined') return false;

    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) return false;

      const response = await axios.post(`${API_URL}/api/v1/auth/refresh`, {
        refresh_token: refreshToken,
      });

      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        if (localStorage.getItem('user_type') === 'super_admin') {
          localStorage.setItem('super_admin_token', response.data.access_token);
        }
        return true;
      }
      return false;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        return false;
      }
      throw new RefreshUnavailableError('Could not reach the refresh endpoint');
    }
  }

  private clearAuthStorage(): void {
    if (typeof window === 'undefined') return;

    const keysToClear = [
      'access_token',
      'refresh_token',
      'super_admin_token',
      'super_admin_refresh_token',
      'tenant_id',
      'user_type',
      'user_role',
      'user_roles',
      'user_id',
      'active_role',
      'user_email',
    ];

    keysToClear.forEach((key) => localStorage.removeItem(key));
  }

  private isSuperAdminRequest(url?: string): boolean {
    if (!url) return false;
    return url.includes('/super-admin/');
  }

  public async login(email: string, password: string, tenant_id?: string) {
    // Auth service expects JSON format with email, password, and optional tenant_id
    const body: { email: string; password: string; tenant_id?: string } = {
      email: email,
      password: password,
    };

    // Include tenant_id if provided (for multi-tenant selection flow)
    if (tenant_id) {
      body.tenant_id = tenant_id;
    }

    const response = await this.client.post('/api/v1/auth/login', body);
    return response.data;
  }

  public async getRedirectUrl(): Promise<string> {
    const response = await this.client.get('/api/v1/auth/redirect-url');
    return response.data.redirect_url;
  }

  public async register(data: {
    tenantName: string;
    tenantCode: string;
    fullName: string;
    email: string;
    password: string;
  }) {
    const response = await this.client.post('/api/v1/auth/register', {
      tenant_name: data.tenantName,
      tenant_code: data.tenantCode,
      full_name: data.fullName,
      email: data.email,
      password: data.password,
    });
    return response.data;
  }

  public async logout() {
    try {
      await this.client.post('/api/v1/auth/logout');
    } catch (error) {
      // Ignore logout errors (e.g., token already expired)
      // We'll clear local storage regardless
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('super_admin_token');
      localStorage.removeItem('super_admin_refresh_token');
      localStorage.removeItem('user_id');
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('user_type');
      localStorage.removeItem('user_role');
    }
  }

  public async getCurrentUser() {
    const response = await this.client.get('/api/v1/auth/me');
    return response.data;
  }

  // Dashboard APIs
  public async getDashboardStats() {
    const response = await this.client.get('/api/v1/dashboard/stats');
    return response.data;
  }

  public async getRecentInspections(limit: number = 5) {
    const response = await this.client.get('/api/v1/inspections/recent', {
      params: { limit },
    });
    return response.data;
  }

  public async getInspectionTrends(days: number = 7) {
    const response = await this.client.get('/api/v1/dashboard/trends', {
      params: { days },
    });
    return response.data;
  }

  public async getActivityFeed(limit: number = 10) {
    const response = await this.client.get('/api/v1/dashboard/activity', {
      params: { limit },
    });
    return response.data;
  }

  // Inspection APIs
  public async getInspections(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }) {
    const response = await this.client.get('/api/v1/inspections', { params });
    return response.data;
  }

  public async getInspection(id: string) {
    const response = await this.client.get(`/api/v1/inspections/${id}`);
    return response.data;
  }

  public async createInspection(data: {
    vehicle_vin: string;
    vehicle_make: string;
    vehicle_model: string;
    vehicle_year: number;
    vehicle_plate?: string;
    vehicle_color?: string;
    vehicle_mileage?: number;
    inspection_type: string;
    location: string;
    notes?: string;
  }) {
    const response = await this.client.post('/api/v1/inspections', data);
    return response.data;
  }

  public async updateInspection(id: string, data: Partial<{
    vehicle_vin: string;
    vehicle_make: string;
    vehicle_model: string;
    vehicle_year: number;
    vehicle_plate?: string;
    vehicle_color?: string;
    vehicle_mileage?: number;
    inspection_type: string;
    location: string;
    notes?: string;
    status: string;
  }>) {
    const response = await this.client.put(`/api/v1/inspections/${id}`, data);
    return response.data;
  }

  public async deleteInspection(id: string) {
    const response = await this.client.delete(`/api/v1/inspections/${id}`);
    return response.data;
  }

  public async uploadInspectionPhoto(inspectionId: string, file: File, metadata?: Record<string, unknown>) {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata) {
      formData.append('metadata', JSON.stringify(metadata));
    }

    const response = await this.client.post(
      `/api/v1/inspections/${inspectionId}/photos`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload progress: ${progress}%`);
          }
        },
      }
    );
    return response.data;
  }

  // Vehicle APIs
  public async getVehicles(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }) {
    const response = await this.client.get('/api/v1/vehicles', { params });
    return response.data;
  }

  public async getVehicle(id: string) {
    const response = await this.client.get(`/api/v1/vehicles/${id}`);
    return response.data;
  }

  public async createVehicle(data: {
    vin: string;
    make: string;
    model: string;
    year: number;
    plate: string;
    color?: string;
    mileage?: number;
    engine_number?: string;
    transmission_type?: string;
    fuel_type?: string;
    owner_name?: string;
    owner_contact?: string;
    owner_email?: string;
    purchase_date?: string;
    notes?: string;
    status: string;
  }) {
    const response = await this.client.post('/api/v1/vehicles', data);
    return response.data;
  }

  public async updateVehicle(id: string, data: Partial<{
    vin: string;
    make: string;
    model: string;
    year: number;
    plate: string;
    color?: string;
    mileage?: number;
    engine_number?: string;
    transmission_type?: string;
    fuel_type?: string;
    owner_name?: string;
    owner_contact?: string;
    owner_email?: string;
    purchase_date?: string;
    notes?: string;
    status: string;
  }>) {
    const response = await this.client.put(`/api/v1/vehicles/${id}`, data);
    return response.data;
  }

  public async deleteVehicle(id: string) {
    const response = await this.client.delete(`/api/v1/vehicles/${id}`);
    return response.data;
  }

  public async getVehicleInspections(vehicleId: string) {
    const response = await this.client.get(`/api/v1/vehicles/${vehicleId}/inspections`);
    return response.data;
  }

  // Storage APIs
  public async uploadFile(file: File, path?: string) {
    const formData = new FormData();
    formData.append('file', file);
    if (path) {
      formData.append('path', path);
    }

    const response = await this.client.post('/api/v1/storage/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${progress}%`);
        }
      },
    });
    return response.data;
  }

  public async downloadFile(fileId: string) {
    const response = await this.client.get(`/api/v1/storage/download/${fileId}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  public async deleteFile(fileId: string) {
    const response = await this.client.delete(`/api/v1/storage/${fileId}`);
    return response.data;
  }

  // AI APIs
  public async analyzeDamage(inspectionId: string, photoIds: string[]) {
    try {
      const response = await this.client.post('/api/v1/ai/analyze-damage', {
        inspection_id: inspectionId,
        photo_ids: photoIds,
      });
      return response.data;
    } catch (error) {
      // Fallback to mock AI service if API is unavailable
      console.warn('AI API not available, using mock service:', error);
      const { mockAIService } = await import('./mock-ai-service');
      return await mockAIService.analyzeDamage(inspectionId, photoIds);
    }
  }

  public async getDamageAnalysis(analysisId: string) {
    try {
      const response = await this.client.get(`/api/v1/ai/analysis/${analysisId}`);
      return response.data;
    } catch (error) {
      // Fallback to mock AI service if API is unavailable
      console.warn('AI API not available, using mock service:', error);
      const { mockAIService } = await import('./mock-ai-service');
      return await mockAIService.getDamageAnalysis(analysisId);
    }
  }

  // Super Admin APIs
  public async superAdminLogin(email: string, password: string) {
    const response = await this.client.post('/api/v1/super-admin/login', {
      email: email,
      password: password,
    });
    return response.data;
  }

  public async getSuperAdminProfile() {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get('/api/v1/super-admin/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  public async getAllTenants(params?: { skip?: number; limit?: number; search?: string }) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get('/api/v1/super-admin/tenants', {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Map backend field names to frontend expected names
    const data = response.data;
    if (data.tenants && Array.isArray(data.tenants)) {
      data.tenants = data.tenants.map((tenant: Record<string, unknown>) => ({
        ...tenant,
        code: tenant.tenant_code || tenant.code,
        name: tenant.company_name || tenant.name,
        contact_email: tenant.support_email || tenant.primary_contact_email || tenant.contact_email,
        contact_phone: tenant.support_phone || tenant.primary_contact_phone || tenant.contact_phone,
      }));
    }

    return data;
  }

  // List tenants for platform users (platform_admin, claims_adjuster, verifier)
  public async listTenants(params?: { skip?: number; limit?: number }) {
    const response = await this.client.get('/api/v1/tenants/', { params });
    // Response is already an array of tenants
    return response.data;
  }

  // Generic getTenants - works for both super admin and platform users
  public async getTenants(params?: { skip?: number; limit?: number; search?: string }) {
    const userType = this.getUserType();

    if (userType === 'super_admin') {
      // Super admins use getAllTenants which returns { tenants: [], total: number }
      const data = await this.getAllTenants(params);
      return data.tenants || [];
    } else {
      // Platform users use listTenants which returns an array directly
      return await this.listTenants(params);
    }
  }

  // Job Lookups
  public async getJobTypes() {
    const response = await this.client.get('/api/v1/lookups/job-types');
    return response.data;
  }

  public async getPriorities() {
    const response = await this.client.get('/api/v1/lookups/priorities');
    return response.data;
  }

  public async getJobStatuses() {
    const response = await this.client.get('/api/v1/lookups/statuses');
    return response.data;
  }

  public async getVehicleTypes() {
    const response = await this.client.get('/api/v1/lookups/vehicle-types');
    return response.data;
  }

  public async getTenantMembers(tenantId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get(`/api/v1/super-admin/tenants/${tenantId}/members`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  public async createTenant(data: {
    name: string;
    code: string;
    legal_name: string;
    industry?: string | undefined;
    country: string;
    timezone: string;
    currency: string;
    contact_email: string;
    contact_phone?: string | undefined;
    address?: string | undefined;
    city?: string | undefined;
    state?: string | undefined;
    postal_code?: string | undefined;
    admin_email: string;
    admin_name: string;
    admin_phone?: string | undefined;
    enable_preinspection_payment_gateway?: boolean | undefined;  // CR-008
  }) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');

    // Map industry to tenant_type (convert spaces to underscores and lowercase)
    const tenantType = data.industry
      ? data.industry.toLowerCase().replace(/\s+/g, '_')
      : 'other';

    // Transform frontend field names to match backend expectations
    const backendData = {
      company_name: data.name,  // Backend expects company_name
      tenant_code: data.code,    // Backend expects tenant_code
      legal_name: data.legal_name,
      country: data.country,
      timezone: data.timezone,
      currency: data.currency,
      tenant_type: tenantType,  // Map industry to tenant_type
      primary_contact_name: data.admin_name,  // Save admin name as primary contact
      primary_contact_email: data.contact_email,
      primary_contact_phone: data.contact_phone,
      // CR-008: Payment gateway pre-inspection trigger
      enable_preinspection_payment_gateway: data.enable_preinspection_payment_gateway ?? false,
      // Note: address fields and admin user creation will need separate API calls
    };

    const response = await this.client.post('/api/v1/super-admin/tenants', backendData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Map backend field names to frontend expected names
    const tenant = response.data;
    return {
      ...tenant,
      code: tenant.tenant_code || tenant.code,
      name: tenant.company_name || tenant.name,
      contact_email: tenant.support_email || tenant.primary_contact_email || tenant.contact_email,
      contact_phone: tenant.support_phone || tenant.primary_contact_phone || tenant.contact_phone,
    };
  }

  public async invitePartnerAdmin(partnerId: string, data: {
    full_name: string;
    email: string;
    phone_number?: string;
    organization_name: string;
  }) {
    const response = await this.client.post(`/api/v1/partners/${partnerId}/invite-admin`, data);
    return response.data;
  }

  public async inviteTenantAdmin(tenantId: string, data: {
    full_name: string;
    email: string;
    phone_number?: string | undefined;
    organization_name: string;
  }) {
    const response = await this.client.post(`/api/v1/tenants/${tenantId}/invite-admin`, data);
    return response.data;
  }

  // ========== Tenant Onboarding Wizard APIs (CR-006 Phase 2 Feature 7) ==========

  /**
   * Get current user's onboarding draft (for resume capability)
   */
  public async getOnboardingDraft() {
    const response = await this.client.get('/api/v1/admin/onboarding/drafts/current');
    return response.data; // Returns null if no draft exists
  }

  /**
   * Save/create onboarding draft (auto-save)
   * If a draft already exists for the user, it will be updated instead of creating a new one
   */
  public async saveOnboardingDraft(data: {
    current_step: number;
    data: Record<string, unknown>;
  }) {
    const response = await this.client.post('/api/v1/admin/onboarding/drafts', data);
    return response.data;
  }

  /**
   * Update existing onboarding draft
   */
  public async updateOnboardingDraft(draftId: string, data: {
    current_step?: number;
    data?: Record<string, unknown>;
  }) {
    const response = await this.client.put(`/api/v1/admin/onboarding/drafts/${draftId}`, data);
    return response.data;
  }

  /**
   * Delete onboarding draft (soft delete)
   */
  public async deleteOnboardingDraft(draftId: string) {
    await this.client.delete(`/api/v1/admin/onboarding/drafts/${draftId}`);
  }

  /**
   * Complete onboarding (atomic tenant creation)
   */
  public async completeOnboarding(data: {
    draft_id?: string;
    data: Record<string, unknown>;
  }) {
    const response = await this.client.post('/api/v1/admin/onboarding/complete', data);
    return response.data;
  }

  /**
   * Validate tenant name uniqueness
   */
  public async validateTenantName(name: string): Promise<{ is_valid: boolean; message: string }> {
    const response = await this.client.get('/api/v1/admin/onboarding/validate/tenant-name', {
      params: { name },
    });
    return response.data;
  }

  /**
   * Validate email uniqueness
   */
  public async validateEmail(email: string): Promise<{ is_valid: boolean; message: string }> {
    const response = await this.client.get('/api/v1/admin/onboarding/validate/email', {
      params: { email },
    });
    return response.data;
  }

  /** Fetch own tenant info — works for any authenticated user, not just super admins. */
  public async getMyTenantInfo(tenantId: string): Promise<{ name: string }> {
    const response = await this.client.get(`/api/v1/tenants/${tenantId}`);
    const t = response.data;
    return { name: t.company_name || t.tenant_name || t.name || tenantId };
  }

  public async getTenant(tenantId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get(`/api/v1/super-admin/tenants/${tenantId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const tenant = response.data;
    const mapped = {
      id: tenant.id,
      code: tenant.tenant_code || tenant.code,
      name: tenant.company_name || tenant.name,
      company_name: tenant.company_name || tenant.name,
      tenant_code: tenant.tenant_code || tenant.code,
      industry: tenant.industry,
      contact_email: tenant.contact_email,
      contact_phone: tenant.contact_phone,
      legal_name: tenant.legal_name,
      tenant_type: tenant.tenant_type,
      country: tenant.country,
      timezone: tenant.timezone,
      currency: tenant.currency,
      language: tenant.language,
      support_email: tenant.support_email,
      support_phone: tenant.support_phone,
      subscription_plan: tenant.subscription_plan,
      subscription_status: tenant.subscription_status,
      subscription_start_date: tenant.subscription_start_date,
      subscription_end_date: tenant.subscription_end_date,
      max_users: tenant.max_users,
      is_active: tenant.is_active,
      is_suspended: tenant.is_suspended,
      primary_contact_name: tenant.primary_contact_name,
      primary_contact_email: tenant.primary_contact_email,
      primary_contact_phone: tenant.primary_contact_phone,
      logo_url: tenant.logo_url,
      primary_color: tenant.primary_color,
      secondary_color: tenant.secondary_color,
      created_by: tenant.created_by,
      created_at: tenant.created_at,
      updated_at: tenant.updated_at,
    };

    return {
      ...mapped,
      contact_email:
        tenant.primary_contact_email || tenant.support_email || tenant.contact_email || '',
      contact_phone:
        tenant.primary_contact_phone || tenant.support_phone || tenant.contact_phone || '',
    };
  }

  public async updateTenant(tenantId: string, data: Partial<{
    name: string;
    legal_name: string;
    industry: string;
    gstin: string;
    pan: string;
    primary_contact_name: string;
    primary_contact_email: string;
    primary_contact_phone: string;
    support_email: string;
    support_phone: string;
    country: string;
    timezone: string;
    currency: string;
    language: string;
    subscription_plan: string;
    subscription_status: string;
    subscription_start_date: string;
    subscription_end_date: string;
    max_users: number;
    is_active: boolean;
    is_suspended: boolean;
    primary_color: string;
    secondary_color: string;
  }>) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const payload: Record<string, unknown> = {};

    if (data.name !== undefined) payload.company_name = data.name;
    if (data.legal_name !== undefined) payload.legal_name = data.legal_name;
    if (data.industry !== undefined) {
      // Map industry to tenant_type (convert spaces to underscores and lowercase)
      payload.tenant_type = data.industry.toLowerCase().replace(/\s+/g, '_');
    }
    if (data.gstin !== undefined) payload.gstin = data.gstin;
    if (data.pan !== undefined) payload.pan = data.pan;
    if (data.primary_contact_name !== undefined) payload.primary_contact_name = data.primary_contact_name;
    if (data.primary_contact_email !== undefined) payload.primary_contact_email = data.primary_contact_email;
    if (data.primary_contact_phone !== undefined) payload.primary_contact_phone = data.primary_contact_phone;
    if (data.support_email !== undefined) payload.support_email = data.support_email;
    if (data.support_phone !== undefined) payload.support_phone = data.support_phone;
    if (data.country !== undefined) payload.country = data.country;
    if (data.timezone !== undefined) payload.timezone = data.timezone;
    if (data.currency !== undefined) payload.currency = data.currency;
    if (data.language !== undefined) payload.language = data.language;
    if (data.subscription_plan !== undefined) payload.subscription_plan = data.subscription_plan;
    if (data.subscription_status !== undefined) payload.subscription_status = data.subscription_status;
    if (data.subscription_start_date !== undefined) payload.subscription_start_date = data.subscription_start_date;
    if (data.subscription_end_date !== undefined) payload.subscription_end_date = data.subscription_end_date;
    if (data.max_users !== undefined) payload.max_users = data.max_users;
    if (data.is_active !== undefined) payload.is_active = data.is_active;
    if (data.is_suspended !== undefined) payload.is_suspended = data.is_suspended;
    if (data.primary_color !== undefined) payload.primary_color = data.primary_color;
    if (data.secondary_color !== undefined) payload.secondary_color = data.secondary_color;

    const response = await this.client.patch(`/api/v1/super-admin/tenants/${tenantId}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  public async updateTenantBranding(tenantId: string, data: {
    primary_color?: string | undefined;
    secondary_color?: string | undefined;
  }) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.put(`/api/v1/super-admin/tenants/${tenantId}/branding`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  public async deleteTenant(tenantId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.delete(`/api/v1/super-admin/tenants/${tenantId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  public async uploadTenantLogo(tenantId: string, file: File) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const formData = new FormData();
    formData.append('logo', file);

    const response = await this.client.post(
      `/api/v1/super-admin/tenants/${tenantId}/branding/logo`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  // ========== Tenant Admin Settings APIs - CR-006-U156 ==========

  /**
   * Get current tenant's settings (tenant admin)
   */
  public async getTenantSettings() {
    const response = await this.client.get('/api/v1/tenant/settings');
    return response.data;
  }

  /**
   * Update current tenant's settings (tenant admin)
   */
  public async updateTenantSettings(data: {
    name?: string;
    contact_email?: string;
    contact_phone?: string;
    primary_color?: string;
    secondary_color?: string;
  }) {
    const response = await this.client.put('/api/v1/tenant/settings', data);
    return response.data;
  }

  /**
   * Upload logo for current tenant (tenant admin)
   */
  public async uploadMyTenantLogo(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post('/api/v1/tenant/settings/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Delete logo for current tenant (tenant admin)
   */
  public async deleteMyTenantLogo() {
    const response = await this.client.delete('/api/v1/tenant/settings/logo');
    return response.data;
  }

  // ========== Tenant Work Order Settings — Payment Type ==========

  /** Get a tenant's Work Order Payment Type settings (mandatory flag + default). */
  public async getTenantWorkOrderSettings(tenantId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get(
      `/api/v1/insurance/tenant-claim-settings/tenants/${tenantId}/work-order-settings`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data as {
      tenant_id: string;
      payment_type_mandatory: boolean;
      default_payment_type: 'cashless' | 'reimbursement' | null;
    };
  }

  /** Update (or create) a tenant's Work Order Payment Type settings. */
  public async updateTenantWorkOrderSettings(
    tenantId: string,
    data: { payment_type_mandatory?: boolean; default_payment_type?: 'cashless' | 'reimbursement' | null },
  ) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.put(
      `/api/v1/insurance/tenant-claim-settings/tenants/${tenantId}/work-order-settings`,
      data,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data as {
      tenant_id: string;
      payment_type_mandatory: boolean;
      default_payment_type: 'cashless' | 'reimbursement' | null;
    };
  }

  // ========== Tenant Billing APIs — Vehicle Claims ==========

  /** Get flat professional charges for a tenant. */
  public async getTenantProfessionalCharges(tenantId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get(
      `/api/v1/insurance/tenant-billing/tenants/${tenantId}/professional-charges`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data as {
      id: string;
      tenant_id: string;
      fee_two_wheeler: number;
      fee_other_than_two_wheeler: number;
      fee_commercial_vehicle: number;
      travelling_expenses: number;
      company_code: string;
      bank_name: string | null;
      bank_account_no: string | null;
      bank_branch: string | null;
      bank_ifsc: string | null;
      company_address: string | null;
      company_gst_number: string | null;
      is_active: boolean;
    };
  }

  /** Create professional charges (POST) or update them (PUT) for a tenant. */
  public async upsertTenantProfessionalCharges(
    tenantId: string,
    data: {
      fee_two_wheeler: number;
      fee_other_than_two_wheeler: number;
      fee_commercial_vehicle: number;
      travelling_expenses: number;
      company_code?: string;
      bank_name?: string;
      bank_account_no?: string;
      bank_branch?: string;
      bank_ifsc?: string;
      company_address?: string;
      company_gst_number?: string;
    },
    mode: 'create' | 'update' = 'create',
  ) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const url = `/api/v1/insurance/tenant-billing/tenants/${tenantId}/professional-charges`;
    const headers = { Authorization: `Bearer ${token}` };
    const response =
      mode === 'create'
        ? await this.client.post(url, data, { headers })
        : await this.client.put(url, data, { headers });
    return response.data;
  }

  /** List all state billing configs for a tenant. */
  public async getTenantStateBillingConfigs(tenantId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get(
      `/api/v1/insurance/tenant-billing/tenants/${tenantId}/state-configs`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data as Array<{
      id: string;
      tenant_id: string;
      state_code: string;
      state_name: string;
      gst_number: string | null;
      igst_rate: number;
      sgst_rate: number;
      cgst_rate: number;
      billing_address: string | null;
      billing_city: string | null;
      billing_pincode: string | null;
      billing_state: string | null;
      is_active: boolean;
    }>;
  }

  /** Add a new state billing config for a tenant. */
  public async createTenantStateBillingConfig(
    tenantId: string,
    data: {
      state_code: string;
      state_name: string;
      gst_number?: string;
      igst_rate?: number;
      sgst_rate?: number;
      cgst_rate?: number;
      billing_address?: string;
      billing_city?: string;
      billing_city_id?: number;
      billing_pincode?: string;
      billing_state?: string;
    },
  ) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.post(
      `/api/v1/insurance/tenant-billing/tenants/${tenantId}/state-configs`,
      data,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  }

  /** Update an existing state billing config entry, identified by its row id
   * (not state_code — a tenant may have multiple offices sharing one state_code). */
  public async updateTenantStateBillingConfig(
    tenantId: string,
    configId: string,
    data: Partial<{
      state_name: string;
      gst_number: string;
      igst_rate: number;
      sgst_rate: number;
      cgst_rate: number;
      billing_address: string;
      billing_city: string;
      billing_city_id?: number;
      billing_pincode: string;
      billing_state: string;
    }>,
  ) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.put(
      `/api/v1/insurance/tenant-billing/tenants/${tenantId}/state-configs/${configId}`,
      data,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  }

  /** Soft-delete a state billing config entry, identified by its row id. */
  public async deleteTenantStateBillingConfig(tenantId: string, configId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    await this.client.delete(
      `/api/v1/insurance/tenant-billing/tenants/${tenantId}/state-configs/${configId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  /** Get list of active cities, optionally filtered by state code. */
  public async getBillingCities(stateCode?: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get(
      '/api/v1/insurance/tenant-billing/cities',
      {
        params: stateCode ? { state_code: stateCode } : {},
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  }

  /** Get list of active Indian states. */
  public async getBillingStates() {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get(
      '/api/v1/insurance/tenant-billing/states',
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  }

  // ========== Claim Assessment Report — Generate / Generate & Share ==========

  /** Generate the claim's Motor Claim Survey Report PDF (view-only, no
   * email) -- the same report the Report tab's own "Generate Report"
   * button produces (POST .../survey-report/generate). Returns the raw
   * PDF blob so the caller can open it in a new browser tab via
   * URL.createObjectURL. */
  public async generateSurveyReport(jobId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const tenantId = localStorage.getItem('tenant_id') || '';
    const response = await this.client.post(
      `/api/v1/insurance/vehicle-claims/${jobId}/survey-report/generate`,
      null,
      {
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenantId },
        responseType: 'blob',
      },
    );
    return response.data as Blob;
  }

  /** Generate the report and email its download link to every tenant
   * claim-number-prefix recipient whose prefix matches this claim's
   * number (Super Admin > Tenant > Settings tab) -- the claim's own
   * customer/garage/authorized-person email fields are not used. No
   * prefix match / no recipients configured -> `sent: false`, not an
   * error. Each entry in `recipients` carries its own delivery status so
   * the caller can render a per-email delivered/failed log. */
  public async generateAndShareClaimReport(jobId: string, reportType: 'detailed' | 'summary' = 'detailed') {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const tenantId = localStorage.getItem('tenant_id') || '';
    const response = await this.client.post(
      `/api/v1/insurance/vehicle-claims/${jobId}/report/generate-and-share`,
      null,
      {
        params: { report_type: reportType },
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenantId },
      },
    );
    return response.data as {
      link: string | null;
      recipients: Array<{ email: string; role: string; status: 'success' | 'failed'; error: string | null }>;
      sent: boolean;
    };
  }

  /** Full 'Generate Report & Share' send history for this claim, every
   * attempt (not just the most recent) -- backs the Assessment tab's
   * Logs button. */
  public async listClaimReportEmailLogs(jobId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const tenantId = localStorage.getItem('tenant_id') || '';
    const response = await this.client.get(
      `/api/v1/insurance/vehicle-claims/${jobId}/report/email-logs`,
      { headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenantId } },
    );
    return response.data as Array<{
      email: string;
      role: string;
      status: 'success' | 'failed';
      error: string | null;
      sent_at: string;
    }>;
  }

  // ========== Tenant Claim Settings APIs — Super Admin Tenant Settings tab ==========
  // A tenant has zero or more claim-number-prefix "groups" (e.g. prefix "09"
  // for one branch, "10" for another), each with its own set of report
  // recipient emails.

  /** List all claim-number-prefix groups for a tenant, each with its own recipients. */
  public async getTenantClaimPrefixes(tenantId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get(
      `/api/v1/insurance/tenant-claim-settings/tenants/${tenantId}/prefixes`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data as Array<{
      id: string;
      tenant_id: string;
      claim_number_prefix: string;
      is_active: boolean;
      recipients: Array<{
        id: string;
        tenant_id: string;
        claim_settings_id: string;
        email: string;
        is_active: boolean;
      }>;
    }>;
  }
  // NOTE: getTenantClaimPrefixes now returns both active and inactive groups
  // (Active/Inactive is a reversible UI toggle, not a hard delete) -- callers
  // must not assume every returned group is active.

  /** Add a new claim-number-prefix group for a tenant. */
  public async createTenantClaimPrefix(tenantId: string, claimNumberPrefix: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.post(
      `/api/v1/insurance/tenant-claim-settings/tenants/${tenantId}/prefixes`,
      { claim_number_prefix: claimNumberPrefix },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  }

  /** Partially update a claim-number-prefix group -- rename it, toggle
   * is_active, or both at once. Only the fields passed are changed. */
  public async updateTenantClaimPrefix(
    tenantId: string,
    prefixId: string,
    data: { claim_number_prefix?: string; is_active?: boolean },
  ) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.put(
      `/api/v1/insurance/tenant-claim-settings/tenants/${tenantId}/prefixes/${prefixId}`,
      data,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  }

  /** Remove a claim-number-prefix group (and its recipients stop applying). */
  public async deleteTenantClaimPrefix(tenantId: string, prefixId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    await this.client.delete(
      `/api/v1/insurance/tenant-claim-settings/tenants/${tenantId}/prefixes/${prefixId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  /** Add a report recipient email to a specific claim-number-prefix group. */
  public async createTenantPrefixRecipient(tenantId: string, prefixId: string, email: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.post(
      `/api/v1/insurance/tenant-claim-settings/tenants/${tenantId}/prefixes/${prefixId}/recipients`,
      { email },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  }

  /** Remove a report recipient email from a specific claim-number-prefix group. */
  public async deleteTenantPrefixRecipient(tenantId: string, prefixId: string, recipientId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    await this.client.delete(
      `/api/v1/insurance/tenant-claim-settings/tenants/${tenantId}/prefixes/${prefixId}/recipients/${recipientId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  // ========== Tenant Admin Analytics APIs - CR-006-U157 ==========

  /**
   * Get operational analytics overview for current tenant (tenant admin)
   */
  public async getTenantAnalyticsOverview(params?: {
    date_range_start?: string;
    date_range_end?: string;
  }) {
    const response = await this.client.get('/api/v1/tenant/analytics/overview', { params });
    return response.data;
  }

  /**
   * Get time-series analytics data for charts (tenant admin)
   */
  public async getTenantTimeSeriesAnalytics(params?: {
    metric?: 'jobs' | 'completed' | 'active';
    interval?: 'day' | 'week' | 'month';
    start_date?: string;
    end_date?: string;
  }) {
    const response = await this.client.get('/api/v1/tenant/analytics/time-series', { params });
    return response.data;
  }

  /**
   * Get job status breakdown for pie chart (tenant admin)
   */
  public async getTenantJobStatusBreakdown(params?: {
    start_date?: string;
    end_date?: string;
  }) {
    const response = await this.client.get('/api/v1/tenant/analytics/job-status', { params });
    return response.data;
  }

  public async getPlatformStats() {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get('/api/v1/super-admin/platform/stats', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  public getClient() {
    return this.client;
  }
  // Platform setup wizard endpoints
  public async checkInstallation() {
    const response = await this.client.post('/api/v1/setup/check-installation');
    return response.data;
  }

  public async initializePlatform(data: { super_admin_email: string; super_admin_password: string; }) {
    const response = await this.client.post('/api/v1/setup/initialize', data);
    return response.data;
  }

  public async verifyPlatform(code: string) {
    const response = await this.client.post('/api/v1/setup/verify', { code });
    return response.data;
  }

  // ========== Persona-Specific APIs ==========

  // Claims Adjuster / Verifier APIs
  public async getAssignedClaims(params?: {
    page?: number;
    limit?: number;
    status?: string;
    priority?: string;
  }) {
    const response = await this.client.get('/api/v1/adjuster/claims/assigned', { params });
    return response.data;
  }

  public async assignClaimToPartner(claimId: string, partnerId: string, notes?: string) {
    const response = await this.client.post(`/api/v1/adjuster/claims/${claimId}/assign`, {
      partner_id: partnerId,
      notes,
    });
    return response.data;
  }

  public async updateClaimStatus(claimId: string, status: string, notes?: string) {
    const response = await this.client.patch(`/api/v1/adjuster/claims/${claimId}/status`, {
      status,
      notes,
    });
    return response.data;
  }

  public async getAvailablePartners() {
    const response = await this.client.get('/api/v1/adjuster/partners/available');
    return response.data;
  }

  // Insurer APIs
  public async submitClaim(data: {
    policy_number: string;
    vehicle_vin: string;
    vehicle_make: string;
    vehicle_model: string;
    vehicle_year: number;
    incident_date: string;
    incident_description: string;
    damage_type: string;
    estimated_loss?: number;
    attachments?: string[];
  }) {
    const response = await this.client.post('/api/v1/insurer/claims', data);
    return response.data;
  }

  public async getInsurerClaims(params?: {
    page?: number;
    limit?: number;
    status?: string;
    from_date?: string;
    to_date?: string;
  }) {
    const response = await this.client.get('/api/v1/insurer/claims', { params });
    return response.data;
  }

  public async getInsurerClaimDetails(claimId: string) {
    const response = await this.client.get(`/api/v1/insurer/claims/${claimId}`);
    return response.data;
  }

  // Partner APIs
  public async getPartnerAssignments(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) {
    const response = await this.client.get('/api/v1/partner/assignments', { params });
    return response.data;
  }

  public async assignToFieldAgent(assignmentId: string, agentId: string, notes?: string) {
    const response = await this.client.post(`/api/v1/partner/assignments/${assignmentId}/assign-agent`, {
      agent_id: agentId,
      notes,
    });
    return response.data;
  }

  public async getPartnerFieldAgents(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) {
    const response = await this.client.get('/api/v1/partner/field-agents', { params });
    return response.data;
  }

  public async onboardFieldAgent(data: {
    full_name: string;
    email: string;
    phone: string;
    address?: string;
    certifications?: string[];
  }) {
    const response = await this.client.post('/api/v1/partner/field-agents', data);
    return response.data;
  }

  // Field Agent APIs
  public async getAgentAssignments(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) {
    const response = await this.client.get('/api/v1/field-agent/assignments', { params });
    return response.data;
  }

  public async acceptAssignment(assignmentId: string) {
    const response = await this.client.post(`/api/v1/field-agent/assignments/${assignmentId}/accept`);
    return response.data;
  }

  public async submitInspectionReport(assignmentId: string, data: {
    photos: string[];
    findings: string;
    damage_assessment: unknown;
    recommendations?: string;
  }) {
    const response = await this.client.post(`/api/v1/field-agent/assignments/${assignmentId}/report`, data);
    return response.data;
  }

  // Platform Admin APIs
  public async createPlatformUser(data: {
    full_name: string;
    email: string;
    roles: string[];
    phone?: string;
  }) {
    const response = await this.client.post('/api/v1/platform/users', data);
    return response.data;
  }

  public async getPlatformUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
  }) {
    const response = await this.client.get('/api/v1/platform/users', { params });
    return response.data;
  }

  public async updateUserRoles(userId: string, roles: string[]) {
    const response = await this.client.patch(`/api/v1/platform/users/${userId}/roles`, { roles });
    return response.data;
  }

  // ========== Platform Employee APIs ==========

  // Get all employee-related lookups for onboarding form
  public async getEmployeeLookups() {
    const response = await this.client.get('/api/v1/platform/employees/lookups');
    return response.data;
  }

  // Create platform employee (requires approval)
  public async createPlatformEmployee(data: {
    email: string;
    full_name: string;
    phone_number?: string;
    password: string;
    user_type: string;
    job_type_id?: number;
    department?: string;
    designation?: string;
    employment_status?: string;
    manager_id?: string;
    team_id?: string;
    date_of_joining?: string;
    work_location?: string;
  }) {
    const response = await this.client.post('/api/v1/platform/employees', data);
    return response.data;
  }

  // List platform employees with filters
  public async getPlatformEmployees(params?: {
    skip?: number;
    limit?: number;
    approval_status?: string;
    department?: string;
    employment_status?: string;
    search?: string;
  }) {
    const response = await this.client.get('/api/v1/platform/employees', { params });
    return response.data;
  }

  // Get platform employee by ID
  public async getPlatformEmployee(employeeId: string) {
    const response = await this.client.get(`/api/v1/platform/employees/${employeeId}`);
    return response.data;
  }

  // Approve/reject/request revision for employee (super admin only)
  public async approveEmployee(employeeId: string, data: {
    action: 'approve' | 'reject' | 'request_revision';
    notes?: string | undefined;
  }) {
    const response = await this.client.patch(`/api/v1/platform/employees/${employeeId}/approve`, data);
    return response.data;
  }

  // Update platform employee information
  public async updatePlatformEmployee(employeeId: string, data: Partial<{
    full_name: string;
    email: string;
    phone_number: string;
    department: string;
    designation: string;
    employment_status: string;
    work_location: string;
    date_of_joining: string;
    manager_id: string | null;
  }>) {
    const response = await this.client.put(`/api/v1/platform/employees/${employeeId}`, data);
    return response.data;
  }

  // ========== Employee Document Management APIs ==========

  // Upload employee document
  public async uploadEmployeeDocument(employeeId: string, file: File, documentType: string, documentNumber?: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    if (documentNumber) {
      formData.append('document_number', documentNumber);
    }

    const response = await this.client.post(
      `/api/v1/platform/employees/${employeeId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  // Get employee documents
  public async getEmployeeDocuments(employeeId: string) {
    const response = await this.client.get(`/api/v1/platform/employees/${employeeId}/documents`);
    return response.data;
  }

  // Verify employee document
  public async verifyEmployeeDocument(employeeId: string, documentId: string, data: {
    verification_status: 'verified' | 'rejected';
    verification_notes?: string | undefined;
  }) {
    const response = await this.client.post(`/api/v1/platform/employees/${employeeId}/documents/${documentId}/verify`, data);
    return response.data;
  }

  // Delete employee document
  public async deleteEmployeeDocument(employeeId: string, documentId: string) {
    const response = await this.client.delete(`/api/v1/platform/employees/${employeeId}/documents/${documentId}`);
    return response.data;
  }

  // ========== Employee Performance APIs ==========

  // Get employee performance metrics
  public async getEmployeePerformanceMetrics(employeeId: string, params?: {
    period?: 'monthly' | 'quarterly' | 'yearly';
    start_date?: string;
    end_date?: string;
  }) {
    const response = await this.client.get(`/api/v1/platform/employees/${employeeId}/performance`, { params });
    return response.data;
  }

  // Create employee performance review
  public async createEmployeePerformanceReview(employeeId: string, data: {
    review_period: string;
    rating: number;
    strengths?: string;
    areas_for_improvement?: string;
    goals?: string;
    reviewer_notes?: string;
  }) {
    const response = await this.client.post(`/api/v1/platform/employees/${employeeId}/performance/reviews`, data);
    return response.data;
  }

  // Get employee performance reviews
  public async getEmployeePerformanceReviews(employeeId: string, params?: {
    skip?: number;
    limit?: number;
  }) {
    const response = await this.client.get(`/api/v1/platform/employees/${employeeId}/performance/reviews`, { params });
    return response.data;
  }

  // ========== Employee Activity Timeline APIs ==========

  // Get employee activity timeline
  public async getEmployeeActivityTimeline(employeeId: string, params?: {
    skip?: number;
    limit?: number;
    activity_type?: string;
  }) {
    const response = await this.client.get(`/api/v1/platform/employees/${employeeId}/activity`, { params });
    return response.data;
  }

  // ========== Employee Hierarchy APIs ==========

  // Get employee subordinates
  public async getEmployeeSubordinates(employeeId: string, params?: {
    skip?: number;
    limit?: number;
    include_indirect?: boolean;
  }) {
    const response = await this.client.get(`/api/v1/platform/employees/${employeeId}/subordinates`, { params });
    return response.data;
  }

  // ========== Employee Bulk Operations APIs ==========

  // Export employees to CSV
  public async exportEmployees(params?: {
    department?: string | undefined;
    employment_status?: string | undefined;
    approval_status?: string | undefined;
  }) {
    const response = await this.client.get('/api/v1/platform/employees/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  // Import employees from CSV
  public async importEmployees(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post('/api/v1/platform/employees/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Bulk update employee status
  public async bulkUpdateEmployeeStatus(employeeIds: string[], status: string) {
    const response = await this.client.post('/api/v1/platform/employees/bulk-update-status', {
      employee_ids: employeeIds,
      employment_status: status,
    });
    return response.data;
  }

  // ========== Team Management APIs ==========

  // List all teams with filters
  public async getTeams(params?: {
    skip?: number;
    limit?: number;
    department?: string;
    is_active?: boolean;
  }) {
    const response = await this.client.get('/api/v1/teams', { params });
    return response.data;
  }

  // Get team by ID
  public async getTeam(teamId: string) {
    const response = await this.client.get(`/api/v1/teams/${teamId}`);
    return response.data;
  }

  // Create new team
  public async createTeam(data: {
    code: string;
    name: string;
    description?: string | undefined;
    department?: string | undefined;
    team_lead_id?: string | undefined;
    parent_team_id?: string | undefined;
  }) {
    const response = await this.client.post('/api/v1/teams', data);
    return response.data;
  }

  // Update team information
  public async updateTeam(teamId: string, data: Partial<{
    name: string;
    description: string;
    department: string;
    team_lead_id: string;
    parent_team_id: string;
    is_active: boolean;
  }>) {
    const response = await this.client.put(`/api/v1/teams/${teamId}`, data);
    return response.data;
  }

  // Delete team
  public async deleteTeam(teamId: string) {
    const response = await this.client.delete(`/api/v1/teams/${teamId}`);
    return response.data;
  }

  // Get team members
  public async getTeamMembers(teamId: string, params?: {
    skip?: number;
    limit?: number;
  }) {
    const response = await this.client.get(`/api/v1/teams/${teamId}/members`, { params });
    return response.data;
  }

  // ========== User Management APIs ==========

  // List all users (platform admin or super admin)
  public async getUsers(params?: {
    skip?: number;
    limit?: number;
    role?: string;
    is_active?: boolean;
    tenant_id?: string;
    search?: string;
  }) {
    const userType = this.getUserType();

    // Super admins use dedicated endpoint
    if (userType === 'super_admin') {
      const response = await this.client.get('/api/v1/super-admin/users', { params });
      return response.data;
    }

    // Platform admins and others use regular endpoint
    const response = await this.client.get('/api/v1/users', { params });
    return response.data;
  }

  // Get user by ID
  public async getUser(userId: string) {
    const response = await this.client.get(`/api/v1/users/${userId}`);
    return response.data;
  }

  // Create new user
  public async createUser(data: {
    email: string;
    full_name: string;
    password: string;
    user_type: string;
    role: string;  // Required - auto-selected based on user_type
    roles?: string[] | undefined;
    tenant_id?: string | undefined;
    tenant_ids?: string[] | undefined;  // Multi-tenant assignment (garage users)
    garage_mobile?: string | undefined; // Garage users only
    claim_number_prefixes?: string | undefined; // Claim RO users only
  }) {
    const response = await this.client.post('/api/v1/users', data);
    return response.data;
  }

  // Invite user to tenant (CR-006-U155)
  public async inviteUser(data: {
    full_name: string;
    email: string;
    phone_number?: string | undefined;
    user_type?: string | undefined;  // Default: insurer_user
    role?: string | undefined;       // Default: user
  }) {
    const params = new URLSearchParams({
      full_name: data.full_name,
      email: data.email,
      user_type: data.user_type || 'insurer_user',
      role: data.role || 'user',
      ...(data.phone_number && { phone_number: data.phone_number })
    });
    const response = await this.client.post(`/api/v1/users/invite?${params.toString()}`);
    return response.data;
  }

  // Update user information
  public async updateUser(userId: string, data: Partial<{
    full_name: string;
    email: string;
    phone_number: string;
    is_active: boolean;
  }>) {
    const response = await this.client.put(`/api/v1/users/${userId}`, data);
    return response.data;
  }

  // Delete user
  public async deleteUser(userId: string) {
    const response = await this.client.delete(`/api/v1/users/${userId}`);
    return response.data;
  }

  // Reset user password (super admin only)
  public async resetUserPassword(userId: string, newPassword: string) {
    const response = await this.client.patch(
      `/api/v1/super-admin/users/${userId}/password`,
      { new_password: newPassword },
    );
    return response.data;
  }

  // ========== Role & Permission Management APIs ==========

  // Note: getUserRoles endpoint removed - backend doesn't have this endpoint
  // Roles are included in the User object from /auth/me

  // Assign role to user
  public async assignRole(userId: string, role: string) {
    const response = await this.client.post(`/api/v1/users/${userId}/roles`, { role });
    return response.data;
  }

  // Revoke role from user
  public async revokeRole(userId: string, role: string) {
    const response = await this.client.delete(`/api/v1/users/${userId}/roles/${role}`);
    return response.data;
  }

  // Bulk assign roles
  public async bulkAssignRoles(userIds: string[], roles: string[]) {
    const response = await this.client.post('/api/v1/users/bulk-assign-roles', {
      user_ids: userIds,
      roles,
    });
    return response.data;
  }

  // ========== User Impersonation APIs (Super Admin Only) ==========

  /**
   * Start impersonating a user (Super Admin only)
   * Uses backend API to generate impersonation token with embedded admin ID
   */
  public async impersonateUser(userId: string) {
    try {
      // Store original admin credentials before switching
      const originalToken = this.getToken();
      const originalUserType = this.getUserType();
      const originalUserId = this.getUserId();

      if (originalToken) {
        localStorage.setItem('impersonation_original_token', originalToken);
      }
      if (originalUserType) {
        localStorage.setItem('impersonation_original_user_type', originalUserType);
      }
      if (originalUserId) {
        localStorage.setItem('impersonation_original_user_id', originalUserId);
      }

      // Call backend impersonation API
      const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
      const response = await this.client.post(
        `/api/v1/super-admin/users/${userId}/impersonate`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data;

      // Store impersonation tokens from backend
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);

      // Store impersonation state
      localStorage.setItem('is_impersonating', 'true');
      localStorage.setItem('impersonated_user_id', data.impersonated_user_id);
      localStorage.setItem('impersonated_user_name', data.impersonated_user_name);
      localStorage.setItem('impersonated_user_email', data.impersonated_user_email);
      localStorage.setItem('impersonation_started_at', new Date().toISOString());

      // Set user context from token
      localStorage.setItem('user_id', data.impersonated_user_id);

      return {
        success: true,
        user: {
          id: data.impersonated_user_id,
          email: data.impersonated_user_email,
          full_name: data.impersonated_user_name,
        },
        message: `Now impersonating ${data.impersonated_user_name}`,
        expires_at: data.expires_at,
      };
    } catch (error) {
      console.error('Error starting impersonation:', error);
      throw error;
    }
  }

  /**
   * Stop impersonating and return to original admin session
   */
  public async stopImpersonation() {
    try {
      // Call backend to properly terminate impersonation session and log audit trail
      const response = await this.client.post('/api/v1/super-admin/impersonation/stop');
      const data = response.data;

      // Restore original admin credentials
      const originalToken = localStorage.getItem('impersonation_original_token');
      const originalUserType = localStorage.getItem('impersonation_original_user_type');
      const originalUserId = localStorage.getItem('impersonation_original_user_id');

      if (originalToken) {
        localStorage.setItem('access_token', originalToken);
        localStorage.setItem('super_admin_token', originalToken);
      }
      if (originalUserType) {
        localStorage.setItem('user_type', originalUserType);
      }
      if (originalUserId) {
        localStorage.setItem('user_id', originalUserId);
      }

      // Clear impersonation state
      localStorage.removeItem('is_impersonating');
      localStorage.removeItem('impersonated_user_id');
      localStorage.removeItem('impersonated_user_name');
      localStorage.removeItem('impersonated_user_email');
      localStorage.removeItem('impersonation_started_at');
      localStorage.removeItem('impersonation_original_token');
      localStorage.removeItem('impersonation_original_user_type');
      localStorage.removeItem('impersonation_original_user_id');
      localStorage.removeItem('tenant_id'); // Clear impersonated tenant

      return {
        success: true,
        message: data.message || 'Impersonation ended successfully',
        duration_seconds: data.impersonation_duration_seconds,
      };
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      // Still try to restore admin session locally even if backend call fails
      const originalToken = localStorage.getItem('impersonation_original_token');
      if (originalToken) {
        localStorage.setItem('access_token', originalToken);
        localStorage.setItem('super_admin_token', originalToken);
      }
      throw error;
    }
  }

  /**
   * Check if currently impersonating a user
   */
  public isImpersonating(): boolean {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('is_impersonating') === 'true';
  }

  /**
   * Get impersonated user info
   */
  public getImpersonatedUserInfo(): {
    userId: string | null;
    userName: string | null;
    userEmail: string | null;
  } {
    if (typeof window === 'undefined' || !this.isImpersonating()) {
      return { userId: null, userName: null, userEmail: null };
    }

    return {
      userId: localStorage.getItem('impersonated_user_id'),
      userName: localStorage.getItem('impersonated_user_name'),
      userEmail: localStorage.getItem('impersonated_user_email'),
    };
  }

  /**
   * Get audit logs with optional filters
   */
  public async getAuditLogs(options?: {
    tenantId?: string | undefined;
    userId?: string | undefined;
    actionCode?: string | undefined;
    entityType?: string | undefined;
    limit?: number | undefined;
    offset?: number | undefined;
  }) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const params: Record<string, unknown> = {
      limit: options?.limit || 50,
      offset: options?.offset || 0,
    };
    if (options?.tenantId) params.tenant_id = options.tenantId;
    if (options?.userId) params.user_id = options.userId;
    if (options?.actionCode) params.action_code = options.actionCode;
    if (options?.entityType) params.entity_type = options.entityType;

    const response = await this.client.get('/api/v1/super-admin/audit-logs', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  /**
   * Get audit logs for a specific tenant
   */
  public async getTenantAuditLogs(tenantId: string, options?: { limit?: number; offset?: number }) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get(`/api/v1/super-admin/tenants/${tenantId}/audit-logs`, {
      params: { limit: options?.limit || 50, offset: options?.offset || 0 },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  /**
   * Get audit logs for a specific user
   */
  public async getUserAuditLogs(userId: string, options?: { limit?: number; offset?: number }) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get(`/api/v1/super-admin/users/${userId}/audit-logs`, {
      params: { limit: options?.limit || 50, offset: options?.offset || 0 },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  // Get user permissions
  public async getUserPermissions(userId: string) {
    const response = await this.client.get(`/api/v1/users/${userId}/permissions`);
    return response.data;
  }

  // Grant permission to user
  public async grantPermission(userId: string, permission: string) {
    const response = await this.client.post(`/api/v1/users/${userId}/permissions`, { permission });
    return response.data;
  }

  // ========== User Tenant Membership APIs - CR-006-U152 ==========

  /**
   * Get user's tenant memberships
   * Returns list of tenants the user belongs to with their roles
   */
  public async getUserTenantMemberships(userId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get(`/api/v1/super-admin/users/${userId}/tenants`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  /**
   * Add user to a tenant with specified role
   */
  public async addUserToTenant(userId: string, data: {
    tenant_id: string;
    role: string;
    is_default?: boolean;
  }) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.post(
      `/api/v1/super-admin/users/${userId}/tenants`,
      {
        tenant_id: data.tenant_id,
        role: data.role,
        is_default: data.is_default || false,
        is_active: true,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Remove user from a tenant
   */
  public async removeUserFromTenant(userId: string, tenantId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.delete(
      `/api/v1/super-admin/users/${userId}/tenants/${tenantId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Get user activity logs
   * Returns recent actions performed by the user
   */
  public async getUserActivityLogs(userId: string, options?: { limit?: number; offset?: number }) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const params: Record<string, unknown> = {
      limit: options?.limit || 50,
      offset: options?.offset || 0,
    };
    const response = await this.client.get(`/api/v1/super-admin/users/${userId}/activity`, {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  /**
   * Alias for getUser - used by some components
   */
  public async getUserById(userId: string) {
    return this.getUser(userId);
  }

  // ========== Super Admin Analytics APIs - CR-006-U153 ==========

  /**
   * Get platform-wide overview analytics
   * Returns total tenants, active tenants, total users, total jobs with trends
   */
  public async getSuperAdminAnalytics(filters?: {
    dateRange?: { start: string; end: string };
    tenantId?: string;
  }) {
    const params: Record<string, unknown> = {};
    if (filters?.dateRange) {
      params.date_range_start = filters.dateRange.start;
      params.date_range_end = filters.dateRange.end;
    }

    const response = await this.client.get('/api/v1/super-admin/analytics/overview', { params });
    return response.data;
  }

  /**
   * Get per-tenant metrics with sorting and pagination
   */
  public async getTenantMetrics(options?: {
    sortBy?: string;
    sortDesc?: boolean;
    skip?: number;
    limit?: number;
  }) {
    const params: Record<string, unknown> = {
      sort_by: options?.sortBy || 'job_count',
      sort_desc: options?.sortDesc !== false,
      skip: options?.skip || 0,
      limit: options?.limit || 20,
    };

    const response = await this.client.get('/api/v1/super-admin/analytics/tenants', { params });
    return response.data;
  }

  /**
   * Get time-series analytics data for charts
   */
  public async getTimeSeriesAnalytics(options: {
    metric: 'tenants' | 'users' | 'jobs';
    interval?: 'day' | 'week' | 'month';
    startDate?: string;
    endDate?: string;
  }) {
    const params: Record<string, unknown> = {
      metric: options.metric,
      interval: options.interval || 'day',
    };

    if (options.startDate) params.start_date = options.startDate;
    if (options.endDate) params.end_date = options.endDate;

    const response = await this.client.get('/api/v1/super-admin/analytics/time-series', { params });
    return response.data;
  }

  // Revoke permission from user
  public async revokePermission(userId: string, permission: string) {
    const response = await this.client.delete(`/api/v1/users/${userId}/permissions/${permission}`);
    return response.data;
  }

  // Get all available roles
  public async getRoles() {
    const response = await this.client.get('/api/v1/roles');
    return response.data;
  }

  // Get all available permissions
  public async getPermissions() {
    const response = await this.client.get('/api/v1/permissions');
    return response.data;
  }

  // Get role permissions
  public async getRolePermissions(role: string) {
    const response = await this.client.get(`/api/v1/roles/${role}/permissions`);
    return response.data;
  }

  // ========== Partner Management APIs (Platform Admin) ==========

  // List partners with filters
  public async getPartners(params?: {
    skip?: number;
    limit?: number;
    partner_type?: string;
    status?: string;
    is_verified?: boolean;
  }) {
    const response = await this.client.get('/api/v1/partners/', { params });
    return response.data;
  }

  // Get partner by ID
  public async getPartner(partnerId: string) {
    const response = await this.client.get(`/api/v1/partners/${partnerId}`);
    return response.data;
  }

  // Create a new partner
  public async createPartner(data: {
    business_name: string;
    trade_name?: string | undefined;
    email: string;
    phone: string;
    alternate_phone?: string | undefined;
    website?: string | undefined;
    address_line1?: string | undefined;
    address_line2?: string | undefined;
    city?: string | undefined;
    state?: string | undefined;
    pincode?: string | undefined;
    country?: string | undefined;
    business_type?: string | undefined;
    partner_type: string;
    gstin?: string | undefined;
    pan?: string | undefined;
  }) {
    const response = await this.client.post('/api/v1/partners/', data);
    return response.data;
  }

  // Update partner information
  public async updatePartner(partnerId: string, data: Partial<{
    business_name: string;
    trade_name: string;
    email: string;
    phone: string;
    alternate_phone: string;
    website: string;
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
    business_type: string;
    gstin: string;
    pan: string;
  }>) {
    const response = await this.client.put(`/api/v1/partners/${partnerId}`, data);
    return response.data;
  }

  // Upload partner document
  public async uploadPartnerDocument(partnerId: string, file: File, documentTypeCode: string, documentNumber?: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentTypeCode);
    if (documentNumber) {
      formData.append('document_number', documentNumber);
    }

    const response = await this.client.post(
      `/api/v1/partners/${partnerId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  // Get partner documents
  public async getPartnerDocuments(partnerId: string) {
    const response = await this.client.get(`/api/v1/partners/${partnerId}/documents`);
    return response.data;
  }

  // Verify partner document
  public async verifyPartnerDocument(partnerId: string, documentId: string, data: {
    verification_status: 'verified' | 'rejected';
    verification_notes?: string | undefined;
  }) {
    const response = await this.client.post(`/api/v1/partners/${partnerId}/documents/${documentId}/verify`, data);
    return response.data;
  }

  // Get field agents for a specific partner (by partner ID)
  public async getFieldAgentsByPartner(partnerId: string, params?: {
    skip?: number;
    limit?: number;
    is_active?: boolean;
  }) {
    const response = await this.client.get(`/api/v1/partners/${partnerId}/field-agents`, { params });
    return response.data;
  }

  // Create field agent (by partner admin)
  public async createFieldAgent(partnerId: string, data: {
    full_name: string;
    email: string;
    mobile: string;
    alternate_mobile?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    pincode?: string;
    specializations?: string[]; // Array of specialization codes
    years_of_experience?: number;
    qualification?: string;
    aadhar_number?: string;
    pan_number?: string;
    driving_license_number?: string;
  }) {
    const response = await this.client.post(`/api/v1/partners/${partnerId}/field-agents`, data);
    return response.data;
  }

  // Get partner performance metrics
  public async getPartnerPerformanceMetrics(partnerId: string, params?: {
    period?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    start_date?: string;
    end_date?: string;
  }) {
    const response = await this.client.get(`/api/v1/partners/${partnerId}/performance`, { params });
    return response.data;
  }

  // Get partner ratings and reviews
  public async getPartnerRatings(partnerId: string, params?: {
    skip?: number;
    limit?: number;
  }) {
    const response = await this.client.get(`/api/v1/partners/${partnerId}/ratings`, { params });
    return response.data;
  }

  // Search partners
  public async searchPartners(query: string, limit?: number) {
    const response = await this.client.get('/api/v1/partners/search', {
      params: { q: query, limit: limit || 20 },
    });
    return response.data;
  }

  // ========== Partner KYC Verification APIs ==========

  // Get pending KYC verifications
  public async getPendingKYCVerifications(params?: {
    verification_level_id?: number;
    limit?: number;
    offset?: number;
  }) {
    const response = await this.client.get('/api/v1/partners/kyc/pending', { params });
    return response.data;
  }

  // Verify partner document (KYC)
  public async verifyDocument(documentId: string, data: {
    verification_level_id: number;
    verification_notes?: string | undefined;
    expiry_checked?: boolean | undefined;
    authenticity_checked?: boolean | undefined;
    compliance_checked?: boolean | undefined;
  }, verifiedBy: string) {
    const response = await this.client.post(
      `/api/v1/partners/kyc/documents/${documentId}/verify`,
      data,
      { params: { verified_by: verifiedBy } }
    );
    return response.data;
  }

  // Reject partner document (KYC)
  public async rejectDocument(documentId: string, data: {
    rejection_reason_id: number;
    verification_notes?: string;
  }, verifiedBy: string) {
    const response = await this.client.post(
      `/api/v1/partners/kyc/documents/${documentId}/reject`,
      data,
      { params: { verified_by: verifiedBy } }
    );
    return response.data;
  }

  // Request document resubmission
  public async requestDocumentResubmission(documentId: string, data: {
    rejection_reason_id: number;
    verification_notes?: string;
  }, requestedBy: string) {
    const response = await this.client.post(
      `/api/v1/partners/kyc/documents/${documentId}/request-resubmission`,
      data,
      { params: { requested_by: requestedBy } }
    );
    return response.data;
  }

  // Get verification history
  public async getVerificationHistory(documentId: string) {
    const response = await this.client.get(`/api/v1/partners/kyc/documents/${documentId}/history`);
    return response.data;
  }

  // Get KYC verification levels
  public async getKYCVerificationLevels() {
    const response = await this.client.get('/api/v1/partners/kyc/verification-levels');
    return response.data;
  }

  // Get KYC verification statuses
  public async getKYCVerificationStatuses() {
    const response = await this.client.get('/api/v1/partners/kyc/verification-statuses');
    return response.data;
  }

  // Get rejection reasons
  public async getRejectionReasons() {
    const response = await this.client.get('/api/v1/partners/kyc/rejection-reasons');
    return response.data;
  }

  // ========== Partner Onboarding APIs ==========

  // Get partner onboarding progress
  public async getPartnerOnboarding(partnerId: string) {
    const response = await this.client.get(`/api/v1/partners/${partnerId}/onboarding`);
    return response.data;
  }

  // Advance onboarding stage
  public async advanceOnboardingStage(partnerId: string, nextStageId: number) {
    const response = await this.client.post(`/api/v1/partners/${partnerId}/onboarding/advance`, {
      next_stage_id: nextStageId
    });
    return response.data;
  }

  // Update stage progress
  public async updateStageProgress(partnerId: string, stageId: number, data: {
    status_id?: number;
    completion_percentage?: number;
    notes?: string;
  }) {
    const response = await this.client.patch(
      `/api/v1/partners/${partnerId}/onboarding/stages/${stageId}`,
      data
    );
    return response.data;
  }

  // Get onboarding checklist
  public async getOnboardingChecklist(partnerId: string, stageId?: number) {
    const response = await this.client.get(
      `/api/v1/partners/${partnerId}/onboarding/checklist`,
      { params: { stage_id: stageId } }
    );
    return response.data;
  }

  // Complete checklist item
  public async completeChecklistItem(partnerId: string, itemId: string, completedBy: string) {
    const response = await this.client.post(
      `/api/v1/partners/${partnerId}/onboarding/checklist/${itemId}/complete`,
      {},
      { params: { completed_by: completedBy } }
    );
    return response.data;
  }

  // Get onboarding stages
  public async getOnboardingStages() {
    const response = await this.client.get('/api/v1/partners/onboarding/stages');
    return response.data;
  }

  // Get onboarding stage statuses
  public async getOnboardingStageStatuses() {
    const response = await this.client.get('/api/v1/partners/onboarding/stage-statuses');
    return response.data;
  }

  // ========== Partner Approval APIs ==========

  // Approve partner
  public async approvePartner(partnerId: string, data: {
    approved_by: string;
    approval_notes?: string;
  }) {
    const response = await this.client.post(`/api/v1/partners/${partnerId}/approve`, data);
    return response.data;
  }

  // Reject partner
  public async rejectPartner(partnerId: string, data: {
    rejected_by: string;
    rejection_reason?: string;
  }) {
    const response = await this.client.post(`/api/v1/partners/${partnerId}/reject`, data);
    return response.data;
  }

  // Request partner revision
  public async requestPartnerRevision(partnerId: string, data: {
    requested_by: string;
    revision_notes: string;
  }) {
    const response = await this.client.post(`/api/v1/partners/${partnerId}/request-revision`, data);
    return response.data;
  }

  // ========== Jobs API ==========

  // Create a new job
  public async createJob(data: {
    tenant_id: string;
    capture_flow_id: number;
    job_type: string;
    priority: string;
    vehicle: {
      registration_number: string;
      vin?: string | undefined;
      make?: string | undefined;
      model?: string | undefined;
      year?: number | undefined;
      type?: string | undefined;
    };
    customer: {
      name: string;
      email?: string | undefined;
      phone?: string | undefined;
      address?: Record<string, unknown> | undefined;
    };
    incident?: {
      date?: string | undefined;
      location?: Record<string, unknown> | undefined;
      description?: string | undefined;
    } | undefined;
    external_ref_number?: string | undefined;
    policy_number?: string | undefined;
    metadata?: Record<string, unknown> | undefined;
  }) {
    const response = await this.client.post('/api/v1/jobs/', data);
    return response.data;
  }

  // Get job by ID
  public async getJob(jobId: string) {
    const response = await this.client.get(`/api/v1/jobs/${jobId}`);
    return response.data;
  }

  // List jobs with filters
  public async listJobs(params?: {
    status?: string;
    assigned_to?: string;
    verified?: boolean;
    skip?: number;
    limit?: number;
  }) {
    const response = await this.client.get('/api/v1/jobs/', { params });
    return response.data;
  }

  // ========== Self-Inspection APIs - CR-006 Phase 2 Feature 1 (U211) ==========

  /**
   * Create self-inspection job (Internal Staff - Insurer User)
   * Generates session token for customer portal access
   */
  public async createSelfInspectionJob(data: {
    customer: {
      name: string;
      email: string;
      phone: string;
    };
    vehicle: {
      registration_number: string;
      make?: string | undefined;
      model?: string | undefined;
      year?: number | undefined;
    };
    claim: {
      claim_id: string;
      incident_date: string;
      incident_description: string;
    };
    report_template_type?: string;
  }) {
    const response = await this.client.post('/api/v1/self-inspection/jobs', data);
    return response.data;
  }

  /**
   * Get self-inspection session details by job ID (Internal Staff)
   */
  public async getSelfInspectionSession(jobId: string) {
    const response = await this.client.get(`/api/v1/self-inspection/jobs/${jobId}/session`);
    return response.data;
  }

  /**
   * Request OTP for customer portal access (Customer - No Auth)
   * @param sessionToken - Session token from email/SMS
   * @param deliveryMethod - 'email' or 'sms'
   */
  public async requestSelfInspectionOTP(sessionToken: string, deliveryMethod: 'email' | 'sms' = 'email') {
    const response = await this.client.post(
      `/api/v1/self-inspection/portal/${sessionToken}/otp/request`,
      { delivery_method: deliveryMethod }
    );
    return response.data;
  }

  /**
   * Verify OTP and get authenticated session (Customer - No Auth)
   * @param sessionToken - Session token from email/SMS
   * @param otpCode - 6-digit OTP code
   */
  public async verifySelfInspectionOTP(sessionToken: string, otpCode: string) {
    const response = await this.client.post(
      `/api/v1/self-inspection/portal/${sessionToken}/otp/verify`,
      { otp_code: otpCode }
    );
    return response.data;
  }

  /**
   * Get upload session status and progress (Customer)
   * @param sessionToken - Session token from email/SMS
   */
  public async getSelfInspectionStatus(sessionToken: string) {
    const response = await this.client.get(`/api/v1/self-inspection/portal/${sessionToken}/status`);
    return response.data;
  }

  /**
   * Upload media files (photos/videos) for self-inspection (Customer)
   * @param sessionToken - Session token from email/SMS
   * @param files - Array of File objects (max 10 files)
   */
  public async uploadSelfInspectionMedia(sessionToken: string, files: File[]) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await this.client.post(
      `/api/v1/self-inspection/portal/${sessionToken}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  /**
   * Submit self-inspection for processing (Customer)
   * Triggers AI damage detection and job processing
   * @param sessionToken - Session token from email/SMS
   */
  public async submitSelfInspection(sessionToken: string) {
    const response = await this.client.post(`/api/v1/self-inspection/portal/${sessionToken}/submit`);
    return response.data;
  }

  /**
   * List all self-inspection jobs (Internal Staff - Super Admin/Insurer)
   * @param params - Filter params (status, tenant_id, search, skip, limit)
   */
  public async listSelfInspectionJobs(params?: {
    status?: string;
    tenant_id?: string;
    search?: string;
    skip?: number;
    limit?: number;
  }) {
    const response = await this.client.get('/super-admin/jobs', { params });
    return response.data;
  }

  /**
   * Get job details by ID (Internal Staff)
   * @param jobId - Job UUID
   */
  public async getJobDetails(jobId: string) {
    const response = await this.client.get(`/super-admin/jobs/${jobId}`);
    return response.data;
  }

  /**
   * Send session link to customer via email/SMS (Internal Staff)
   * @param jobId - Job UUID
   * @param method - 'email' or 'sms'
   */
  public async sendSelfInspectionLink(jobId: string, method: 'email' | 'sms') {
    const response = await this.client.post(`/api/v1/self-inspection/jobs/${jobId}/send-link`, { method });
    return response.data;
  }

  // ========== Lookup Tables CRUD APIs - CR-006 Phase 3 U322 ==========

  /**
   * List all rows from a lookup table with pagination
   * @param table - Table name (e.g., 'user_types', 'job_statuses')
   * @param params - Optional filters (is_active, page, limit)
   */
  public async listLookupTableRows(table: string, params?: {
    is_active?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }) {
    const response = await this.client.get(`/api/v1/lookups/admin/${table}`, { params });
    return response.data;
  }

  /**
   * Create a new row in a lookup table
   * @param table - Table name (e.g., 'user_types', 'job_statuses')
   * @param data - Row data to create
   */
  public async createLookupTableRow(table: string, data: {
    code: string;
    name: string;
    description?: string | undefined;
    is_active?: boolean | undefined;
    display_order?: number | undefined;
    additional_fields?: Record<string, unknown> | undefined;
  }) {
    const response = await this.client.post(`/api/v1/lookups/admin/${table}`, data);
    return response.data;
  }

  /**
   * Update an existing row in a lookup table
   * @param table - Table name (e.g., 'user_types', 'job_statuses')
   * @param id - Row ID to update
   * @param data - Updated row data
   */
  public async updateLookupTableRow(table: string, id: string, data: {
    code?: string | undefined;
    name?: string | undefined;
    description?: string | undefined;
    is_active?: boolean | undefined;
    display_order?: number | undefined;
    additional_fields?: Record<string, unknown> | undefined;
  }) {
    const response = await this.client.put(`/api/v1/lookups/admin/${table}/${id}`, data);
    return response.data;
  }

  /**
   * Delete (soft delete) a row in a lookup table
   * @param table - Table name (e.g., 'user_types', 'job_statuses')
   * @param id - Row ID to delete
   */
  public async deleteLookupTableRow(table: string, id: string) {
    const response = await this.client.delete(`/api/v1/lookups/admin/${table}/${id}`);
    return response.data;
  }

  /**
   * Reorder rows in a lookup table (CR-006 Phase 3 U323)
   * @param table - Table name (e.g., 'user_types', 'job_statuses')
   * @param reorderedIds - Array of row IDs in the new order
   */
  public async reorderLookupTableRows(table: string, reorderedIds: string[]) {
    const response = await this.client.patch(`/api/v1/lookups/admin/${table}/reorder`, {
      reordered_ids: reorderedIds
    });
    return response.data;
  }

  /**
   * Import CSV file to lookup table (CR-006 Phase 3 U323)
   * @param table - Table name (e.g., 'user_types', 'job_statuses')
   * @param file - CSV file to import
   */
  public async importLookupTableCSV(table: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post(`/api/v1/lookups/admin/${table}/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  /**
   * List pending approval requests (CR-006 Phase 3 U325)
   * @param page - Page number
   * @param limit - Items per page
   */
  public async listPendingApprovals(page: number = 1, limit: number = 50) {
    const response = await this.client.get('/api/v1/lookups/admin/approvals/pending', {
      params: { page, limit }
    });
    return response.data;
  }

  /**
   * Review (approve or reject) a change request (CR-006 Phase 3 U325)
   * @param changeRequestId - Change request ID
   * @param action - APPROVE or REJECT
   * @param reviewNotes - Optional review notes
   */
  public async reviewChangeRequest(changeRequestId: string, action: 'APPROVE' | 'REJECT', reviewNotes?: string) {
    const response = await this.client.post(`/api/v1/lookups/admin/approvals/${changeRequestId}/review`, {
      action,
      review_notes: reviewNotes
    });
    return response.data;
  }

  /**
   * Get foreign key references for a lookup table row (CR-006 Phase 3 U326)
   */
  public async getLookupTableRowReferences(table: string, id: string, page: number = 1, pageSize: number = 100) {
    const response = await this.client.get(`/api/v1/lookups/admin/${table}/${id}/references`, {
      params: { page, page_size: pageSize }
    });
    return response.data;
  }

  // ========== UseCase Configuration APIs - CR-006 Phase 3 U361 ==========

  /**
   * Get all use case configurations for a tenant
   * Returns all job types with their enabled status and settings
   * @param tenantId - Optional tenant ID (defaults to current tenant from JWT)
   */
  public async getTenantUseCases(tenantId?: string) {
    const params: Record<string, unknown> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await this.client.get('/api/v1/tenant-admin/use-cases', { params });
    return response.data;
  }

  /**
   * Update use case configuration for a tenant
   * Creates or updates the configuration for a specific job type
   * @param jobTypeId - Job type ID
   * @param data - Configuration update (enabled status and settings)
   * @param tenantId - Optional tenant ID (defaults to current tenant from JWT)
   */
  public async updateTenantUseCase(jobTypeId: number, data: {
    enabled: boolean;
    settings?: Record<string, unknown>;
  }, tenantId?: string) {
    const params: Record<string, unknown> = {};
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await this.client.put(
      `/api/v1/tenant-admin/use-cases/${jobTypeId}`,
      data,
      { params }
    );
    return response.data;
  }

  /**
   * Bulk update use case configurations for a tenant
   * Updates multiple use cases at once by enabling specified job types
   * @param tenantId - Tenant ID
   * @param data - Object with use_cases array of job type codes to enable
   */
  public async updateTenantUseCases(tenantId: string, data: { use_cases: string[] }) {
    console.log('[updateTenantUseCases] Starting for tenant:', tenantId);
    console.log('[updateTenantUseCases] Use cases to enable:', data.use_cases);

    // Get all use cases to find job type IDs
    const allUseCases = await this.getTenantUseCases(tenantId);
    console.log('[updateTenantUseCases] Fetched use cases:', allUseCases.length);

    // Update each use case
    const updatePromises = allUseCases.map((uc: Record<string, unknown>) => {
      const shouldEnable = data.use_cases.includes(uc.job_type_code as string);
      console.log(`[updateTenantUseCases] ${uc.job_type_code} (ID: ${uc.job_type_id}) -> ${shouldEnable ? 'ENABLE' : 'DISABLE'}`);
      return this.updateTenantUseCase(
        uc.job_type_id as number,
        {
          enabled: shouldEnable,
          settings: (uc.settings as Record<string, unknown>) || {},
        },
        tenantId
      ).catch(err => {
        console.error(`[updateTenantUseCases] Failed to update ${uc.job_type_code}:`, err);
        throw err;
      });
    });

    await Promise.all(updatePromises);
    console.log('[updateTenantUseCases] All use cases updated successfully');
    return { success: true, message: 'Use cases updated successfully' };
  }

  /**
   * Get use case configurations for any tenant (Super Admin only)
   * @param tenantId - Tenant ID
   */
  public async getSuperAdminTenantUseCases(tenantId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const response = await this.client.get(`/api/v1/admin/tenants/${tenantId}/use-cases`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  // ========== Claim Status APIs ==========

  /**
   * Merged, active claim status list for a tenant + usecase (job_type).
   * Used by the Claim Details tab's status dropdown/badge.
   * @param jobTypeCode - Job type / usecase code, e.g. 'VEHICLE_CLAIM'
   * @param tenantId - Optional tenant ID (defaults to current tenant from JWT)
   */
  public async listClaimStatuses(jobTypeCode: string, tenantId?: string) {
    const params: any = { job_type_code: jobTypeCode };
    if (tenantId) {
      params.tenant_id = tenantId;
    }
    const response = await this.client.get('/api/v1/claim-statuses', { params });
    return response.data;
  }

  /**
   * Admin management view for a tenant's claim statuses (includes inactive
   * rows). Used by ClaimStatusManager.
   * @param tenantId - Tenant ID
   * @param jobTypeId - Job type / usecase id
   * @param includeInactive - Include deactivated statuses (default false)
   */
  public async listClaimStatusesAdmin(tenantId: string, jobTypeId: number, includeInactive = false) {
    const params: any = { job_type_id: jobTypeId, include_inactive: includeInactive };
    const response = await this.client.get(`/api/v1/admin/tenants/${tenantId}/claim-statuses`, { params });
    return response.data;
  }

  /**
   * Create a new claim status for a tenant + usecase. Superadmin callers
   * create scope='global' rows; tenant admins create scope='tenant' rows —
   * the server decides scope from the caller's role.
   */
  public async createClaimStatus(tenantId: string, data: {
    job_type_id: number;
    code: string;
    label: string;
    color?: string;
    sort_order?: number;
  }) {
    const response = await this.client.post(`/api/v1/admin/tenants/${tenantId}/claim-statuses`, data);
    return response.data;
  }

  /**
   * Update a claim status's label/color/sort_order/active state. Tenant
   * admins may only update scope='tenant' rows they own; only super admin
   * may update scope='global' rows.
   */
  public async updateClaimStatus(tenantId: string, statusId: number, data: {
    label?: string;
    color?: string;
    sort_order?: number;
    is_active?: boolean;
  }) {
    const response = await this.client.patch(`/api/v1/admin/tenants/${tenantId}/claim-statuses/${statusId}`, data);
    return response.data;
  }

  // ========== OCR / Job Media Methods ==========

  /**
   * Trigger batch OCR text extraction for job media
   */
  public async batchExtractJobMedia(jobId: string, data: {
    media_ids: string[];
    document_type: string;
    detection_type: string;
  }) {
    const response = await this.client.post(
      `/api/v1/jobs/${jobId}/media/extract-text-batch`,
      data
    );
    return response.data;
  }

  /**
   * Get OCR status for all job media
   */
  public async getJobMediaStatuses(jobId: string) {
    const response = await this.client.get(`/api/v1/jobs/${jobId}/media`);
    return response.data;
  }

  /**
   * Get OCR task status by task ID (for standalone OCR without job context)
   */
  public async getOCRTaskStatus(taskId: string) {
    const response = await this.client.get(`/api/v1/ai/ocr/status/${taskId}`);
    return response.data;
  }

  // ========== Billing Summary APIs -- Financer role ==========
  // Every claim in the tenant with a Billing State set (Assessment tab),
  // joined with its Bill Invoice if one has been generated yet. Financer's
  // entire screen -- see services/insurance-service/app/api/v1/
  // claim_billing_summary.py.

  public async getBillingSummary(filters?: {
    payment_status?: 'pending' | 'paid';
    invoice_date_from?: string;
    invoice_date_to?: string;
  }) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const tenantId = localStorage.getItem('tenant_id') || '';
    const response = await this.client.get(
      '/api/v1/insurance/vehicle-claims/billing-summary',
      {
        params: filters,
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenantId },
      },
    );
    return response.data as Array<{
      job_id: string;
      report_number: string;
      claim_number: string;
      vehicle_registration_no: string;
      invoice_number: string | null;
      client_gst_number: string | null;
      bill_amount_without_gst: number | null;
      igst_amount: number | null;
      cgst_amount: number | null;
      sgst_amount: number | null;
      final_total: number | null;
      invoice_date: string | null;
      payment_status: 'pending' | 'paid' | null;
      payment_received_date: string | null;
    }>;
  }

  /** Same rows/filters as getBillingSummary, as an .xlsx blob. */
  public async exportBillingSummary(filters?: {
    payment_status?: 'pending' | 'paid';
    invoice_date_from?: string;
    invoice_date_to?: string;
  }) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const tenantId = localStorage.getItem('tenant_id') || '';
    const response = await this.client.get(
      '/api/v1/insurance/vehicle-claims/billing-summary/export',
      {
        params: filters,
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenantId },
        responseType: 'blob',
      },
    );
    return response.data as Blob;
  }

  /** Read-only render of the already-generated Bill Invoice PDF for the
   * "view invoice" icon on this screen -- never mutates the invoice row
   * (unlike claim_bill_invoice.py's staff "Generate Bill Invoice" action,
   * which would move generated_at). 404 if no invoice yet. */
  public async getBillingInvoicePdf(jobId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const tenantId = localStorage.getItem('tenant_id') || '';
    const response = await this.client.get(
      `/api/v1/insurance/vehicle-claims/${jobId}/billing-summary/invoice-pdf`,
      {
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenantId },
        responseType: 'blob',
      },
    );
    return response.data as Blob;
  }

  /** Feeds the "Payment Update" modal -- the invoice's own Sub Total /
   * GST / Total plus whatever payment breakdown has already been
   * recorded (pre-fills the form on re-edit). 404 if no invoice yet. */
  public async getBillingSummaryDetail(jobId: string) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const tenantId = localStorage.getItem('tenant_id') || '';
    const response = await this.client.get(
      `/api/v1/insurance/vehicle-claims/${jobId}/billing-summary/detail`,
      { headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenantId } },
    );
    return response.data as BillingInvoiceDetail;
  }

  /** Records the "Payment Update" modal's reconciliation and marks the
   * invoice Paid -- submitting this IS the mark-as-paid action, there's
   * no separate status toggle. */
  public async updateBillingPayment(
    jobId: string,
    data: {
      received_fees_amount?: number | null;
      received_gst_amount?: number | null;
      received_tds_amount?: number | null;
      received_short_amount?: number | null;
      payment_details?: string | null;
      payment_received_date: string;
    },
  ) {
    const token = localStorage.getItem('super_admin_token') || localStorage.getItem('access_token');
    const tenantId = localStorage.getItem('tenant_id') || '';
    const response = await this.client.patch(
      `/api/v1/insurance/vehicle-claims/${jobId}/billing-summary/payment`,
      data,
      { headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenantId } },
    );
    return response.data as BillingInvoiceDetail;
  }

  // ========== Utility Methods ==========

  public setActiveRole(role: string) {
    if (typeof window !== 'undefined') {
      localStorage.setItem('active_role', role);
    }
  }

  public clearActiveRole() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('active_role');
    }
  }

  // ========== Generic HTTP Methods ==========
  // Used by service modules that need direct HTTP access

  public async get<T = unknown>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get(url, { params });
    return response.data;
  }

  public async post<T = unknown>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.post(url, data);
    return response.data;
  }

  public async put<T = unknown>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.put(url, data);
    return response.data;
  }

  public async patch<T = unknown>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.patch(url, data);
    return response.data;
  }

  public async delete<T = unknown>(url: string): Promise<T> {
    const response = await this.client.delete(url);
    return response.data;
  }

}

export const apiClient = new ApiClient();
export default apiClient;
